/** Default page size for the passage editor's associated-questions grid. */
export const defaultAssociatedQuestionPageSize = 10;

/** Returns one 1-based page of `items`; an out-of-range page returns []. */
export const paginate = <T>(
  items: Array<T>,
  pageNumber: number,
  pageSize: number
): Array<T> => {
  if (pageNumber < 1 || pageSize < 1) return [];
  const start = (pageNumber - 1) * pageSize;
  return items.slice(start, start + pageSize);
};

/** Number of pages needed for `totalRecords`; 0 when there is nothing to page. */
export const getTotalPages = (totalRecords: number, pageSize: number): number =>
  totalRecords > 0 && pageSize > 0 ? Math.ceil(totalRecords / pageSize) : 0;

/** The 1-based first/last record numbers shown on a page, for "Showing x–y of N". */
export const getPageRange = (
  totalRecords: number,
  pageNumber: number,
  pageSize: number
): { from: number; to: number } => {
  if (totalRecords <= 0) return { from: 0, to: 0 };
  const from = Math.min((pageNumber - 1) * pageSize + 1, totalRecords);
  const to = Math.min(pageNumber * pageSize, totalRecords);
  return { from, to };
};

const htmlEntities: Record<string, string> = {
  "&nbsp;": " ",
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
};

/**
 * Turns a stored question stem (Learnosity HTML) into one line of plain text
 * for the grid: strips tags, decodes common entities, collapses whitespace.
 */
export const toPlainText = (html: string | null | undefined): string => {
  if (!html) return "";
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;|&amp;|&lt;|&gt;|&quot;|&#39;/g, (e) => htmlEntities[e])
    .replace(/\s+/g, " ")
    .trim();
};
